import {
  Room,
  RoomEvent,
  VideoPresets,
  ScreenSharePresets,
  RemoteTrack,
  RemoteParticipant,
  Participant,
  ConnectionQuality,
  RemoteTrackPublication,
  LocalTrackPublication,
  LocalParticipant,
  TrackPublication,
  Track,
} from 'livekit-client';

export interface ChatMessage {
  id: string;
  senderName: string;
  senderIdentity: string;
  text: string;
  timestamp: string;
  isSelf: boolean;
  file?: {
    name: string;
    size: number;
    type: string;
    dataUrl: string;
    progress?: number;
  };
}

export interface LiveKitCallbacks {
  onParticipantConnected?: (p: RemoteParticipant) => void;
  onParticipantDisconnected?: (p: RemoteParticipant) => void;
  onTrackSubscribed?: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackUnsubscribed?: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackMuted?: (publication: TrackPublication, participant: Participant) => void;
  onTrackUnmuted?: (publication: TrackPublication, participant: Participant) => void;
  onActiveSpeakersChanged?: (speakers: Participant[]) => void;
  onConnectionQualityChanged?: (quality: ConnectionQuality, p: Participant) => void;
  onChatMessage?: (message: ChatMessage) => void;
  onChatFile?: (message: ChatMessage) => void;
  onFileTransferProgress?: (info: {
    transferId: string;
    progress: number;
    name: string;
    size: number;
    senderName: string;
    senderIdentity: string;
    timestamp: string;
    text: string;
    isComplete: boolean;
  }) => void;
  onModerationMessage?: (data: { action: string; targetIdentity?: string; targetName?: string; senderIdentity?: string; newName?: string }) => void;
  onHandRaise?: (data: { identity: string; isRaised: boolean; participantName: string }) => void;
  onReaction?: (data: { identity: string; emoji: string; participantName: string }) => void;
  onAudioPlaybackChanged?: (canPlayback: boolean) => void;
  onTrackPublished?: (publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onTrackUnpublished?: (publication: RemoteTrackPublication, participant: RemoteParticipant) => void;
  onLocalTrackPublished?: (publication: LocalTrackPublication, participant: LocalParticipant) => void;
  onLocalTrackUnpublished?: (publication: LocalTrackPublication, participant: LocalParticipant) => void;
  onScreenShareStatus?: (data: { isSharing: boolean; identity: string; sharerName: string }) => void;
  onRecordingStatus?: (data: { isRecording: boolean; recorderName: string; identity: string; recordingSeconds?: number }) => void;
  onQueryRecordingStatus?: (data: { fromIdentity: string; fromName?: string }) => void;
  onRecordingCommand?: (data: { action: string; requestedBy: string }) => void;
  onWhiteboardDraw?: (data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }) => void;
  onWhiteboardClear?: () => void;
  onWhiteboardToggle?: (data: { isOpen: boolean; senderName: string }) => void;
  onPollLaunch?: (data: { pollId: string; question: string; options: string[]; creatorName: string }) => void;
  onPollVote?: (data: { pollId: string; optionIndex: number; voterName: string; voterIdentity: string }) => void;
  onPollClose?: (data: { pollId: string }) => void;
  onWaitingRoomKnock?: (data: { identity: string; name: string; timestamp: number }) => void;
  onWaitingRoomDecision?: (data: { targetIdentity: string; decision: 'admitted' | 'rejected' }) => void;
  onWaitingRoomMoveBack?: (data: { targetIdentity: string }) => void;
  onHostStatus?: (data: { hostConnected: boolean; hostName: string }) => void;
  onMeetingEndedByHost?: (data: { hostName?: string; reason?: string }) => void;
  onAdminLayoutSync?: (data: { viewMode: 'gallery' | 'speaker' | 'studio'; pinnedIdentity: string | null; pinnedName?: string; adminName?: string; isStageLocked?: boolean; senderIdentity?: string; isPrimaryHost?: boolean }) => void;
  onReconnecting?: () => void;
  onReconnected?: () => void;
  onDisconnected?: () => void;
  onLocalScreenStreamReady?: (stream: MediaStream) => void;
  onLocalScreenShareEnded?: () => void;
  onScreenShareChanged?: (isSharing: boolean) => void;
}

export interface WebRTCStatsSummary {
  connected: boolean;
  connectionState: string;
  rttMs: number;
  outboundBitrateKbps: number;
  inboundBitrateKbps: number;
  outboundResolution?: string;
  outboundFps?: number;
  inboundResolution?: string;
  inboundFps?: number;
  packetLossPercent: number;
  qualityLimitation?: string;
  audioCodec?: string;
  videoCodec?: string;
  serverUrl: string;
}

export class LiveKitClientManager {
  private room: Room | null = null;
  private callbacks: LiveKitCallbacks = {};
  public displayName: string = '';
  public isCameraEnabled = false;
  public isMicEnabled = false;
  public isScreenShareEnabled = false;
  public currentFacingMode: 'user' | 'environment' = 'user';
  private localCameraStream: MediaStream | null = null;
  private localMicStream: MediaStream | null = null;
  private prevStatsTime: number = 0;
  private prevBytesSent: number = 0;
  private prevBytesReceived: number = 0;
  private lastOutboundBitrateKbps: number = 0;
  private lastInboundBitrateKbps: number = 0;
  private localScreenStream: MediaStream | null = null;
  private cachedCameraStream: MediaStream | null = null;
  private cachedCameraMst: MediaStreamTrack | null = null;
  private cachedMicStream: MediaStream | null = null;
  private cachedMicMst: MediaStreamTrack | null = null;
  private cachedScreenStream: MediaStream | null = null;
  private cachedScreenMst: MediaStreamTrack | null = null;
  private fallbackInterval: any = null;
  private incomingFileTransfers = new Map<string, {
    chunks: string[];
    receivedCount: number;
    totalChunks: number;
    metadata: {
      name: string;
      size: number;
      fileType: string;
      text: string;
      timestamp: string;
      senderName: string;
      senderIdentity: string;
    };
    timeoutId: any;
  }>();

  constructor(callbacks: LiveKitCallbacks = {}) {
    this.callbacks = callbacks;
  }

  public getRoom(): Room | null {
    return this.room;
  }

  public getLocalCameraStream(): MediaStream | null {
    if (this.room && this.room.state === 'connected') {
      try {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            if (this.cachedCameraStream && this.cachedCameraMst === mst && this.cachedCameraStream.active) {
              return this.cachedCameraStream;
            }
            this.cachedCameraMst = mst;
            this.cachedCameraStream = new MediaStream([mst]);
            return this.cachedCameraStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalCameraStream error:', e);
      }
    }
    if (this.localCameraStream && this.localCameraStream.active) {
      return this.localCameraStream;
    }
    return null;
  }

  public getLocalMicStream(): MediaStream | null {
    if (this.room && this.room.state === 'connected') {
      try {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            if (this.cachedMicStream && this.cachedMicMst === mst && this.cachedMicStream.active) {
              return this.cachedMicStream;
            }
            this.cachedMicMst = mst;
            this.cachedMicStream = new MediaStream([mst]);
            return this.cachedMicStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalMicStream error:', e);
      }
    }
    if (this.localMicStream && this.localMicStream.active) {
      return this.localMicStream;
    }
    return null;
  }

  public getLocalScreenStream(): MediaStream | null {
    if (this.localScreenStream) {
      const tr = this.localScreenStream.getVideoTracks()[0];
      if (tr && tr.readyState === 'live') {
        return this.localScreenStream;
      }
    }
    if (this.cachedScreenStream && this.cachedScreenMst && this.cachedScreenMst.readyState === 'live') {
      return this.cachedScreenStream;
    }
    if (this.room && this.room.state === 'connected') {
      try {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (pub && pub.track) {
          const mst = (pub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          if (mst && mst.readyState === 'live') {
            this.cachedScreenMst = mst;
            this.cachedScreenStream = new MediaStream([mst]);
            this.localScreenStream = this.cachedScreenStream;
            return this.cachedScreenStream;
          }
        }
      } catch (e) {
        console.warn('[LiveKitClient] getLocalScreenStream error:', e);
      }
    }
    return null;
  }

  public async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<boolean> {
    if (this.room && this.room.state === 'connected') {
      try {
        await this.room.switchActiveDevice(kind, deviceId, true);
        return true;
      } catch (err) {
        console.warn(`[LiveKitClient] switchActiveDevice(${kind}) error:`, err);
        return false;
      }
    }
    return false;
  }

  public async connect(url: string, token: string): Promise<Room> {
    this.room = new Room({
      // LiveKit Server 1.8.3 natively uses Single PeerConnection (Protocol 15/17).
      // Defaults to singlePeerConnection: true. Eliminates the 15-second NegotiationError timeout loop!
      adaptiveStream: true,
      dynacast: true,
      reconnectPolicy: {
        // Aggressive reconnection: retry up to 15 times with increasing delay
        nextRetryDelayInMs: (context: any) => {
          if (context.retryCount > 15) return null; // give up after 15
          return Math.min(500 * Math.pow(1.5, context.retryCount), 10000);
        },
      },
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution,
      },
      publishDefaults: {
        videoEncoding: VideoPresets.h720.encoding,
        simulcast: true,
        screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
        dtx: true,
        red: false,
      },
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    this.setupListeners();

    // Do NOT override iceServers with custom rtcConfig so LiveKit Server's own TURN server (217.217.233.20:3478) is used
    await this.room.connect(url, token, {
      autoSubscribe: true,
    });
    try {
      await this.room.startAudio();
    } catch (e) {
      console.warn('[LiveKitClient] startAudio initial call:', e);
    }

    // Attach global user interaction listeners to immediately unlock audio if browser autoplay blocked it (crucial for PWA, tablets and mobile)
    const unlockAudio = async () => {
      if (this.room && !this.room.canPlaybackAudio) {
        try {
          await this.room.startAudio();
        } catch (_) {}
      }
      if (typeof document !== 'undefined') {
        document.querySelectorAll('audio').forEach((a: any) => {
          if (a.paused && !a.muted) {
            a.play().catch(() => {});
          }
        });
      }
    };
    if (typeof window !== 'undefined') {
      ['touchstart', 'touchend', 'pointerdown', 'click', 'keydown'].forEach(evt => {
        window.addEventListener(evt, unlockAudio, { passive: true });
      });
    }
    return this.room;
  }

  private setupListeners() {
    if (!this.room) return;

    this.room
      .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        this.callbacks.onParticipantConnected?.(p);
      })
      .on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        this.callbacks.onParticipantDisconnected?.(p);
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => {
        this.callbacks.onTrackSubscribed?.(track, pub, p);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => {
        this.callbacks.onTrackUnsubscribed?.(track, pub, p);
      })
      .on(RoomEvent.TrackMuted, (pub: TrackPublication, p: Participant) => {
        this.callbacks.onTrackMuted?.(pub, p);
      })
      .on(RoomEvent.TrackUnmuted, (pub: TrackPublication, p: Participant) => {
        this.callbacks.onTrackUnmuted?.(pub, p);
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        this.callbacks.onActiveSpeakersChanged?.(speakers);
      })
      .on(RoomEvent.ConnectionQualityChanged, (q: ConnectionQuality, p: Participant) => {
        this.callbacks.onConnectionQualityChanged?.(q, p);
      })
      .on(RoomEvent.TrackPublished, (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.info('[LiveKitClient] TrackPublished:', pub.kind, pub.source, 'from', participant.identity);
        if (!pub.isSubscribed) {
          console.info(`[LiveKitClient] Automatically subscribing to ${pub.kind} (${pub.source}) from ${participant.identity}`);
          pub.setSubscribed(true);
        }
        this.callbacks.onTrackPublished?.(pub, participant);
      })
      .on(RoomEvent.TrackUnpublished, (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        console.info('[LiveKitClient] TrackUnpublished:', pub.kind, pub.source, 'from', participant.identity);
        this.callbacks.onTrackUnpublished?.(pub, participant);
      })
      .on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication, participant: LocalParticipant) => {
        console.info('[LiveKitClient] LocalTrackPublished:', pub.kind, pub.source);
        this.callbacks.onLocalTrackPublished?.(pub, participant);
      })
      .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication, participant: LocalParticipant) => {
        console.info('[LiveKitClient] LocalTrackUnpublished:', pub.kind, pub.source);
        this.callbacks.onLocalTrackUnpublished?.(pub, participant);
      })
      .on(RoomEvent.Reconnecting, () => {
        console.warn('[LiveKitClient] Reconnecting...');
        this.callbacks.onReconnecting?.();
      })
      .on(RoomEvent.Reconnected, () => {
        console.info('[LiveKitClient] Reconnected successfully - resyncing all tracks');
        this.reattachAllRemoteTracks();
        this.callbacks.onReconnected?.();
      })
      .on(RoomEvent.Disconnected, () => {
        this.callbacks.onDisconnected?.();
      })
      .on(RoomEvent.MediaDevicesError, (err: Error) => {
        console.error('[LiveKitClient] MediaDevicesError:', err.message);
      })
      .on(RoomEvent.SignalConnected, () => {
        console.info('[LiveKitClient] Signal connected');
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        this.callbacks.onAudioPlaybackChanged?.(this.room?.canPlaybackAudio ?? true);
      })
      .on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant) => {
        try {
          const str = new TextDecoder().decode(payload);
          const data = JSON.parse(str);
          if (data.type === 'chat') {
            this.callbacks.onChatMessage?.({
              id: data.id,
              senderName: data.senderName || participant?.name || 'Participant',
              senderIdentity: participant?.identity || data.senderIdentity || '',
              text: data.text,
              timestamp: data.timestamp,
              isSelf: false,
            });
          } else if (data.type === 'moderation') {
            this.callbacks.onModerationMessage?.({
              action: data.action,
              targetIdentity: data.targetIdentity,
              targetName: data.targetName,
              senderIdentity: participant?.identity || data.senderIdentity || '',
              newName: data.newName,
            });
          } else if (data.type === 'hand_raise') {
            this.callbacks.onHandRaise?.({
              identity: data.identity || participant?.identity || '',
              isRaised: !!data.isRaised,
              participantName: data.name || participant?.name || 'Participant',
            });
          } else if (data.type === 'reaction') {
            this.callbacks.onReaction?.({
              identity: data.identity || participant?.identity || '',
              emoji: data.emoji || '👍',
              participantName: data.name || participant?.name || 'Participant',
            });
          } else if (data.type === 'screen_share_status') {
            this.callbacks.onScreenShareStatus?.({
              isSharing: !!data.isSharing,
              identity: data.identity || participant?.identity || '',
              sharerName: data.sharerName || participant?.name || 'Participant',
            });
          } else if (data.type === 'recording_status') {
            this.callbacks.onRecordingStatus?.({
              isRecording: !!data.isRecording,
              recorderName: data.recorderName || participant?.name || 'Gazda',
              identity: data.identity || participant?.identity || '',
              recordingSeconds: typeof data.recordingSeconds === 'number' ? data.recordingSeconds : undefined,
            });
          } else if (data.type === 'query_recording_status') {
            this.callbacks.onQueryRecordingStatus?.({
              fromIdentity: data.identity || participant?.identity || '',
              fromName: participant?.name || '',
            });
          } else if (data.type === 'recording_command') {
            this.callbacks.onRecordingCommand?.({
              action: data.action || 'stop',
              requestedBy: data.requestedBy || participant?.name || 'Administrator',
            });
          } else if (data.type === 'whiteboard_draw') {
            this.callbacks.onWhiteboardDraw?.({
              x0: data.x0,
              y0: data.y0,
              x1: data.x1,
              y1: data.y1,
              color: data.color || '#38bdf8',
              size: data.size || 3,
              tool: data.tool || 'pen',
            });
          } else if (data.type === 'whiteboard_clear') {
            this.callbacks.onWhiteboardClear?.();
          } else if (data.type === 'whiteboard_toggle') {
            this.callbacks.onWhiteboardToggle?.({
              isOpen: !!data.isOpen,
              senderName: data.senderName || participant?.name || 'Participant',
            });
          } else if (data.type === 'poll_launch') {
            this.callbacks.onPollLaunch?.({
              pollId: data.pollId,
              question: data.question,
              options: data.options || [],
              creatorName: data.creatorName || participant?.name || 'Gazda',
            });
          } else if (data.type === 'poll_vote') {
            this.callbacks.onPollVote?.({
              pollId: data.pollId,
              optionIndex: Number(data.optionIndex),
              voterName: data.voterName || participant?.name || 'Participant',
              voterIdentity: participant?.identity || data.voterIdentity || '',
            });
          } else if (data.type === 'poll_close') {
            this.callbacks.onPollClose?.({
              pollId: data.pollId,
            });
          } else if (data.type === 'chat_file') {
            const chatMsg: ChatMessage = {
              id: data.id,
              senderName: data.senderName || participant?.name || 'Participant',
              senderIdentity: participant?.identity || data.senderIdentity || '',
              text: data.text || '',
              timestamp: data.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isSelf: false,
              file: data.file,
            };
            this.callbacks.onChatMessage?.(chatMsg);
            this.callbacks.onChatFile?.(chatMsg);
          } else if (data.type === 'chat_file_chunk') {
            this.handleIncomingFileChunk(data, participant);
          } else if (data.type === 'waiting_room_knock') {
            this.callbacks.onWaitingRoomKnock?.({
              identity: data.identity || participant?.identity || '',
              name: data.name || participant?.name || 'Participant',
              timestamp: data.timestamp || Date.now(),
            });
          } else if (data.type === 'waiting_room_decision') {
            this.callbacks.onWaitingRoomDecision?.({
              targetIdentity: data.targetIdentity,
              decision: data.decision,
            });
          } else if (data.type === 'waiting_room_move_back') {
            this.callbacks.onWaitingRoomMoveBack?.({
              targetIdentity: data.targetIdentity,
            });
          } else if (data.type === 'host_status') {
            this.callbacks.onHostStatus?.({
              hostConnected: !!data.hostConnected,
              hostName: data.hostName || participant?.name || 'Gazda',
            });
          } else if (data.type === 'meeting_ended_by_host' || data.type === 'room_closed') {
            this.callbacks.onMeetingEndedByHost?.({
              hostName: data.hostName || participant?.name || 'Gazda',
              reason: data.reason || 'Conferința a fost încheiată de către gazdă.',
            });
          } else if (data.type === 'admin_layout_sync') {
            this.callbacks.onAdminLayoutSync?.({
              viewMode: data.viewMode,
              pinnedIdentity: data.pinnedIdentity,
              pinnedName: data.pinnedName,
              adminName: data.adminName,
              isStageLocked: Boolean(data.isStageLocked),
              senderIdentity: data.senderIdentity || participant?.identity,
              isPrimaryHost: Boolean(data.isPrimaryHost),
            });
          }
        } catch (e) {
          console.error('[LiveKitClient] Failed to parse in-room data message', e);
        }
      });
  }

  public setInitialCameraStream(stream: MediaStream | null): void {
    if (stream && stream.active) {
      this.localCameraStream = stream;
      this.isCameraEnabled = true;
      console.info('[LiveKitClient] Transferred active camera stream seamlessly from pre-join');
    }
  }

  public async enableCamera(enable: boolean): Promise<boolean> {
    this.isCameraEnabled = enable;

    if (!this.room || this.room.state !== 'connected') {
      if (!enable) {
        if (this.localCameraStream) {
          try {
            this.localCameraStream.getTracks().forEach(t => t.stop());
          } catch (_) {}
          this.localCameraStream = null;
        }
        return true;
      }

      // Pre-connection or offline fallback: acquire local camera immediately so user NEVER sees a black screen!
      if (!this.localCameraStream || !this.localCameraStream.active) {
        try {
          const preferredCamId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null;
          this.localCameraStream = await acquireUserCameraStream(preferredCamId);
          console.info('[LiveKitClient] Early local camera stream acquired successfully for zero-delay preview');
        } catch (e) {
          console.warn('[LiveKitClient] Failed to acquire early local camera stream:', e);
        }
      }
      return true;
    }

    const pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);

    // MUTE CAMERA (false):
    if (!enable) {
      if (pub) {
        try {
          if (pub.track) {
            pub.track.mediaStreamTrack.enabled = false;
          }
          await pub.mute();
          console.info('[LiveKitClient] Camera muted instantly (track kept alive)');
        } catch (e) {
          console.warn('[LiveKitClient] Error muting camera track:', e);
        }
      }
      try {
        await this.room.localParticipant.setCameraEnabled(false);
      } catch (_) {}
      return true;
    }

    // ENABLE CAMERA (true):
    // 1. Fast path: if track was already published, simply enable track and unmute! (0ms delay)
    if (pub && pub.track) {
      const msTrack = pub.track.mediaStreamTrack;
      if (msTrack && msTrack.readyState === 'live') {
        try {
          msTrack.enabled = true;
          await pub.unmute();
          console.info('[LiveKitClient] Camera unmuted instantly (fast path, 0ms)');
          return true;
        } catch (e) {
          console.warn('[LiveKitClient] Fast unmute error, will re-publish:', e);
        }
      }
    }

    // Seamless camera transition: If we already acquired a live camera track during pre-join,
    // publish it directly to LiveKit without turning off the camera hardware! (0ms flash)
    if (this.localCameraStream) {
      const existingTrack = this.localCameraStream.getVideoTracks()[0];
      if (existingTrack && existingTrack.readyState === 'live') {
        try {
          existingTrack.enabled = true;
          this.cachedCameraMst = existingTrack;
          this.cachedCameraStream = this.localCameraStream;
          this.isCameraEnabled = true;
          const pub = await this.room.localParticipant.publishTrack(existingTrack, {
            name: 'camera',
            source: Track.Source.Camera,
            simulcast: false,
            videoEncoding: VideoPresets.h720.encoding,
          });
          console.info('[LiveKitClient] Camera published seamlessly from active track (0ms flash, no hardware restart):', pub?.trackSid);
          return true;
        } catch (pubErr) {
          console.warn('[LiveKitClient] publishTrack failed for existing camera, falling back to setCameraEnabled:', pubErr);
          try {
            this.localCameraStream.getTracks().forEach(t => t.stop());
          } catch (_) {}
          this.localCameraStream = null;
        }
      } else {
        try {
          this.localCameraStream.getTracks().forEach(t => t.stop());
        } catch (_) {}
        this.localCameraStream = null;
      }
    }

    // Publish camera via LiveKit native setCameraEnabled
    let camOptions: any = undefined;
    const isMobile = typeof navigator !== 'undefined' && (
      /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
    );

    if (isMobile) {
      camOptions = {
        facingMode: this.currentFacingMode || 'user',
        resolution: VideoPresets.h720.resolution,
      };
      console.info(`[LiveKitClient] Mobile camera enable with facingMode: ${this.currentFacingMode || 'user'}`);
    } else {
      let preferredCamId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null;
      camOptions = {
        deviceId: preferredCamId && preferredCamId !== 'default' && preferredCamId.trim() !== '' ? preferredCamId : undefined,
        resolution: VideoPresets.h720.resolution,
      };
    }

    try {
      if (camOptions) {
        await this.room.localParticipant.setCameraEnabled(true, camOptions);
      } else {
        await this.room.localParticipant.setCameraEnabled(true);
      }
      console.info('[LiveKitClient] Camera published to room successfully');
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] setCameraEnabled with deviceId failed, retrying universal default:', err);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('vorbello_selected_cam');
      }
      try {
        await this.room.localParticipant.setCameraEnabled(true);
        console.info('[LiveKitClient] Camera published via default device fallback');
        return true;
      } catch (retryErr) {
        console.error('[LiveKitClient] Failed to publish camera to room:', retryErr);
        return false;
      }
    }
  }

  public async flipCamera(): Promise<boolean> {
    if (!this.room) return false;

    // Case 1: Room connected: switch active device directly (v1.4.5 proven reliable method)
    if (this.room.state === 'connected') {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevs = devices.filter(d => d.kind === 'videoinput');
        if (videoDevs.length < 2) {
          console.warn('[LiveKitClient] Less than 2 video devices available, cannot flip');
          return false;
        }

        const pub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
        const activeTrackDeviceId = pub?.track?.mediaStreamTrack?.getSettings()?.deviceId;
        let currentId = activeTrackDeviceId || (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null);

        let nextIndex = 0;
        if (currentId) {
          const currIdx = videoDevs.findIndex(d => d.deviceId === currentId);
          if (currIdx !== -1) {
            nextIndex = (currIdx + 1) % videoDevs.length;
          }
        }

        const nextDev = videoDevs[nextIndex];
        if (nextDev) {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('vorbello_selected_cam', nextDev.deviceId);
          }
          const label = (nextDev.label || '').toLowerCase();
          if (label.includes('front') || label.includes('user') || label.includes('selfie') || label.includes('anterior') || label.includes('față')) {
            this.currentFacingMode = 'user';
          } else if (label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('spate') || label.includes('posterior')) {
            this.currentFacingMode = 'environment';
          } else {
            this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
          }

          // Native LiveKit device switch without unpublishing/killing the WebRTC track
          await this.room.switchActiveDevice('videoinput', nextDev.deviceId, true);
          this.isCameraEnabled = true;
          console.info('[LiveKitClient] Switched active camera seamlessly to:', nextDev.label || nextDev.deviceId);
          return true;
        }
      } catch (e) {
        console.warn('[LiveKitClient] flipCamera room switch error:', e);
      }
      return false;
    }

    // Case 2: Pre-join or preview mode
    try {
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
      if (this.localCameraStream) {
        try {
          this.localCameraStream.getTracks().forEach(t => t.stop());
        } catch (_) {}
        this.localCameraStream = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: this.currentFacingMode } },
        audio: false,
      });
      this.localCameraStream = stream;
      this.isCameraEnabled = true;
      return true;
    } catch (e) {
      console.warn('[LiveKitClient] flipCamera preview error:', e);
      return false;
    }
  }

  public async enableMicrophone(enable: boolean): Promise<boolean> {
    this.isMicEnabled = enable;

    if (!this.room || this.room.state !== 'connected') {
      return true;
    }

    const pub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);

    if (!enable) {
      if (pub) {
        try {
          if (pub.track) {
            pub.track.mediaStreamTrack.enabled = false;
          }
          await pub.mute();
          console.info('[LiveKitClient] Microphone muted instantly');
        } catch (e) {
          console.warn('[LiveKitClient] Error muting mic track:', e);
        }
      }
      try {
        await this.room.localParticipant.setMicrophoneEnabled(false);
      } catch (_) {}
      return true;
    }

    // ENABLE MICROPHONE (true):
    // 1. Fast path: if track was already published and still live, simply enable track and unmute! (Takes 0ms)
    if (pub && pub.track) {
      const msTrack = pub.track.mediaStreamTrack;
      if (msTrack && msTrack.readyState === 'live') {
        try {
          msTrack.enabled = true;
          await pub.unmute();
          console.info('[LiveKitClient] Microphone unmuted instantly (fast path)');
          return true;
        } catch (e) {
          console.warn('[LiveKitClient] Fast mic unmute error, fallback to setMicrophoneEnabled:', e);
        }
      }
    }

    // 2. Initial publication: select device without pre-locking hardware
    let micOpts: any = undefined;
    let preferredMicId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_mic') : null;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevs = devices.filter(d => d.kind === 'audioinput');
      let selected = audioDevs.find(d => d.deviceId === preferredMicId);
      if (!selected) {
        // Prioritize Yeti Nano or dedicated microphone
        const bestMic = audioDevs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('yeti') || l.includes('nano');
        }) || audioDevs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('micro') || l.includes('angetube');
        }) || audioDevs[0];
        if (bestMic) {
          preferredMicId = bestMic.deviceId;
          localStorage.setItem('vorbello_selected_mic', preferredMicId);
        }
      }
      if (preferredMicId && preferredMicId !== 'default') {
        micOpts = { deviceId: preferredMicId };
      }
    } catch (_) {}

    try {
      await this.room.localParticipant.setMicrophoneEnabled(true, micOpts);
      console.info('[LiveKitClient] Microphone published successfully');
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] setMicrophoneEnabled error, retrying default:', err);
      try {
        await this.room.localParticipant.setMicrophoneEnabled(true);
        return true;
      } catch (retryErr) {
        console.error('[LiveKitClient] Failed to publish microphone to room:', retryErr);
        return false;
      }
    }
  }

  public async enableScreenShare(enable: boolean): Promise<boolean> {
    this.isScreenShareEnabled = enable;

    if (!enable) {
      if (this.room && this.room.state === 'connected') {
        try {
          const screenPubs = Array.from(this.room.localParticipant.trackPublications.values())
            .filter(p => p.source === Track.Source.ScreenShare || p.source === Track.Source.ScreenShareAudio);
          for (const sp of screenPubs) {
            if (sp.track) {
              try {
                await this.room.localParticipant.unpublishTrack(sp.track, true);
                console.info('[LiveKitClient] Explicitly unpublished screen track:', sp.trackSid);
              } catch (unpubErr) {
                console.warn('[LiveKitClient] unpublishTrack error:', unpubErr);
              }
            }
          }
          await this.room.localParticipant.setScreenShareEnabled(false);
          console.info('[LiveKitClient] Screen share disabled successfully');
        } catch (err) {
          console.warn('[LiveKitClient] Error disabling screen share:', err);
        }

        try {
          await this.sendDataMessage({
            type: 'screen_share_status',
            isSharing: false,
            identity: this.room.localParticipant.identity,
          });
        } catch (_) {}
      }

      // Disarm all onended handlers BEFORE stopping tracks to prevent re-entrant loops
      if (this.cachedScreenMst) {
        this.cachedScreenMst.onended = null;
        try { this.cachedScreenMst.stop(); } catch (_) {}
      }
      if (this.cachedScreenStream) {
        try {
          this.cachedScreenStream.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
      }
      if (this.localScreenStream) {
        try {
          this.localScreenStream.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
      }
      this.cachedScreenStream = null;
      this.cachedScreenMst = null;
      this.localScreenStream = null;
      this.callbacks.onLocalScreenShareEnded?.();
      return true;
    }

    // ENABLE SCREEN SHARE:
    if (this.room && this.room.state === 'connected') {
      let stream: MediaStream | null = null;
      try {
        let controller: any = null;
        try {
          if (typeof (window as any).CaptureController !== 'undefined') {
            controller = new (window as any).CaptureController();
          }
        } catch (_) { controller = null; }

        const opts: any = {
          video: {
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 60 }
          },
          audio: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
          systemAudio: 'include'
        };
        if (controller) opts.controller = controller;

        try {
          stream = await navigator.mediaDevices.getDisplayMedia(opts);
        } catch (gdmErr: any) {
          if (gdmErr && (gdmErr.name === 'NotAllowedError' || gdmErr.name === 'AbortError')) throw gdmErr;
          // Fallback to simpler audio options for browsers with stricter constraint parsers
          try {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
          } catch (_) {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
          }
          controller = null;
        }

        if (controller && typeof controller.setFocusBehavior === 'function') {
          try { controller.setFocusBehavior('no-focus-change'); } catch (_) {}
        }

        const mst = stream.getVideoTracks()[0];
        if (!mst) throw new Error('No video track in display stream');
        try { (mst as any).contentHint = 'detail'; } catch (_) {}

        this.cachedScreenMst = mst;
        this.cachedScreenStream = stream;
        this.localScreenStream = stream;
        this.isScreenShareEnabled = true;

        // CRITICAL: Notify UI IMMEDIATELY with local stream so stage renders at 0 ms without waiting for SFU server negotiation!
        this.callbacks.onLocalScreenStreamReady?.(stream);

        mst.onended = async () => {
          console.info('[LiveKitClient] Screen share track ended by user');
          await this.enableScreenShare(false);
          this.callbacks.onLocalScreenShareEnded?.();
        };

        // 2. Publish video track to LiveKit server
        const pub = await this.room.localParticipant.publishTrack(mst, {
          name: 'screen_share',
          source: Track.Source.ScreenShare,
          simulcast: false,
        });
        console.info('[LiveKitClient] Screen share video published:', pub?.trackSid);

        // 3. Publish screen audio track if present (tab audio, system audio, video player audio)
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const screenAudioTrack = audioTracks[0];
          try {
            const audioPub = await this.room.localParticipant.publishTrack(screenAudioTrack, {
              name: 'screen_share_audio',
              source: Track.Source.ScreenShareAudio,
            });
            console.info('[LiveKitClient] Screen share audio published successfully:', audioPub?.trackSid);
          } catch (audioPubErr) {
            console.warn('[LiveKitClient] Failed to publish screen share audio:', audioPubErr);
          }
        } else {
          console.info('[LiveKitClient] No screen audio track provided (user might not have checked "Share audio" in browser dialog)');
        }

        await this.sendDataMessage({
          type: 'screen_share_status',
          isSharing: true,
          identity: this.room.localParticipant.identity,
          sharerName: this.room.localParticipant.name || this.displayName || 'Participant',
        });
        return true;
      } catch (err: any) {
        console.warn('[LiveKitClient] Screen share error:', err);
        try {
          stream?.getTracks().forEach(t => {
            t.onended = null;
            t.stop();
          });
        } catch (_) {}
        this.isScreenShareEnabled = false;
        this.localScreenStream = null;
        this.cachedScreenStream = null;
        this.cachedScreenMst = null;
        return false;
      }
    } else {
      // Offline / Preview fallback
      if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        try {
          let stream: MediaStream;
          try {
            stream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: true,
            });
          } catch (_) {
            stream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: false,
            });
          }
          this.localScreenStream = stream;
          this.cachedScreenStream = stream;
          const tr = stream.getVideoTracks()[0];
          if (tr) {
            this.cachedScreenMst = tr;
            tr.onended = async () => {
              await this.enableScreenShare(false);
              this.callbacks.onLocalScreenShareEnded?.();
            };
          }
          this.isScreenShareEnabled = true;
          this.callbacks.onLocalScreenStreamReady?.(stream);
          return true;
        } catch (e) {
          this.isScreenShareEnabled = false;
          return false;
        }
      }
    }
    return false;
  }

  public setLocalScreenStream(stream: MediaStream | null): void {
    this.localScreenStream = stream;
    this.cachedScreenStream = stream;
    if (stream) {
      this.cachedScreenMst = stream.getVideoTracks()[0] || null;
      this.isScreenShareEnabled = true;
      this.callbacks.onLocalScreenStreamReady?.(stream);
    }
  }

  public async replaceScreenShareTrack(newTrack: MediaStreamTrack, newAudioTrack?: MediaStreamTrack | null): Promise<boolean> {
    try {
      // 1. DISARM & STOP OLD TRACKS: Explicitly stop old video and audio tracks so background tabs (e.g. YouTube) do NOT leak audio!
      if (this.cachedScreenMst && this.cachedScreenMst !== newTrack) {
        this.cachedScreenMst.onended = null;
        try { this.cachedScreenMst.stop(); } catch (_) {}
      }
      if (this.cachedScreenStream) {
        try {
          this.cachedScreenStream.getTracks().forEach(t => {
            if (t !== newTrack && t !== newAudioTrack) {
              t.onended = null;
              try { t.stop(); } catch (_) {}
            }
          });
        } catch (_) {}
      }
      if (this.localScreenStream) {
        try {
          this.localScreenStream.getTracks().forEach(t => {
            if (t !== newTrack && t !== newAudioTrack) {
              t.onended = null;
              try { t.stop(); } catch (_) {}
            }
          });
        } catch (_) {}
      }

      // 2. WIRE NEW TRACK onended
      newTrack.onended = async () => {
        console.info('[LiveKitClient] Replaced screen share track ended by user');
        await this.enableScreenShare(false);
        this.callbacks.onLocalScreenShareEnded?.();
      };

      const tracksForStream = [newTrack];
      if (newAudioTrack) tracksForStream.push(newAudioTrack);
      const newStream = new MediaStream(tracksForStream);
      this.cachedScreenMst = newTrack;
      this.cachedScreenStream = newStream;
      this.localScreenStream = newStream;
      this.isScreenShareEnabled = true;

      // 3. Immediately notify UI so spotlight updates at 0ms!
      this.callbacks.onLocalScreenStreamReady?.(newStream);

      if (this.room && this.room.state === 'connected') {
        const pub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (pub && pub.track) {
          try {
            if (typeof (pub.track as any).replaceTrack === 'function') {
              await (pub.track as any).replaceTrack(newTrack, true);
            } else if ((pub.track as any).sender && typeof (pub.track as any).sender.replaceTrack === 'function') {
              await (pub.track as any).sender.replaceTrack(newTrack);
              (pub.track as any).mediaStreamTrack = newTrack;
              (pub.track as any)._mediaStreamTrack = newTrack;
            } else {
              await this.room.localParticipant.unpublishTrack(pub.track);
              await this.room.localParticipant.publishTrack(newTrack, {
                name: 'screen_share',
                source: Track.Source.ScreenShare,
                simulcast: false,
              });
            }
          } catch (replaceErr) {
            console.warn('[LiveKitClient] replaceTrack error, falling back to republish:', replaceErr);
            try {
              if (pub && pub.track) await this.room.localParticipant.unpublishTrack(pub.track);
            } catch (_) {}
            await this.room.localParticipant.publishTrack(newTrack, {
              name: 'screen_share',
              source: Track.Source.ScreenShare,
              simulcast: false,
            });
          }
        } else {
          await this.room.localParticipant.publishTrack(newTrack, {
            name: 'screen_share',
            source: Track.Source.ScreenShare,
            simulcast: false,
          });
        }

        // 4. Update or publish screen audio track publication
        const audioPub = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio);
        if (newAudioTrack) {
          if (audioPub && audioPub.track) {
            try {
              if (typeof (audioPub.track as any).replaceTrack === 'function') {
                await (audioPub.track as any).replaceTrack(newAudioTrack, true);
              } else {
                try { (audioPub.track as any).mediaStreamTrack?.stop(); audioPub.track.stop(); } catch (_) {}
                await this.room.localParticipant.unpublishTrack(audioPub.track, true);
                await this.room.localParticipant.publishTrack(newAudioTrack, {
                  name: 'screen_share_audio',
                  source: Track.Source.ScreenShareAudio,
                });
              }
            } catch (_) {
              try {
                (audioPub.track as any).mediaStreamTrack?.stop();
                audioPub.track.stop();
                await this.room.localParticipant.unpublishTrack(audioPub.track, true);
              } catch (_) {}
              await this.room.localParticipant.publishTrack(newAudioTrack, {
                name: 'screen_share_audio',
                source: Track.Source.ScreenShareAudio,
              });
            }
          } else {
            try {
              await this.room.localParticipant.publishTrack(newAudioTrack, {
                name: 'screen_share_audio',
                source: Track.Source.ScreenShareAudio,
              });
            } catch (aErr) {
              console.warn('[LiveKitClient] Failed to publish screen audio track on swap:', aErr);
            }
          }
        } else {
          // If the new source does not have audio, explicitly STOP and unpublish any previous screen audio track
          if (audioPub && audioPub.track) {
            try {
              console.info('[LiveKitClient] Stopping and unpublishing old screen audio track on swap to silent source...');
              try { (audioPub.track as any).mediaStreamTrack?.stop(); } catch (_) {}
              try { audioPub.track.stop(); } catch (_) {}
              await this.room.localParticipant.unpublishTrack(audioPub.track, true);
            } catch (_) {}
          }
        }

        try {
          await this.sendDataMessage({
            type: 'screen_audio_status',
            hasAudio: Boolean(newAudioTrack),
            identity: this.room.localParticipant.identity,
          });
        } catch (_) {}
      }
      return true;
    } catch (err) {
      console.warn('[LiveKitClient] replaceScreenShareTrack fatal error:', err);
      return false;
    }
  }

  public async sendDataMessage(data: any): Promise<boolean> {
    if (this.room && this.room.state === 'connected') {
      try {
        const payload = new TextEncoder().encode(JSON.stringify(data));
        await this.room.localParticipant.publishData(payload, { reliable: true });
        return true;
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast data message:', err);
      }
    }
    return false;
  }

  public async sendChatMessage(text: string, customSenderName?: string): Promise<ChatMessage | null> {
    if (!text.trim()) return null;

    const outgoingName = customSenderName || this.displayName || this.room?.localParticipant?.name || 'Tu';
    const message: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      senderName: outgoingName,
      senderIdentity: this.room?.localParticipant?.identity || 'self',
      text: text.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSelf: true,
    };

    if (this.room && this.room.state === 'connected') {
      try {
        const payload = new TextEncoder().encode(JSON.stringify({
          type: 'chat',
          ...message,
          senderName: outgoingName === 'Tu' && this.displayName ? this.displayName : outgoingName,
        }));
        await this.room.localParticipant.publishData(payload, { reliable: true });
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast chat message to remote participants:', err);
      }
    }

    return message;
  }

  public async sendHandRaise(isRaised: boolean): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    const name = this.room?.localParticipant?.name || 'Tu';
    return this.sendDataMessage({
      type: 'hand_raise',
      identity,
      name,
      isRaised,
      timestamp: Date.now(),
    });
  }

  public async sendLowerAllHands(): Promise<boolean> {
    const name = this.room?.localParticipant?.name || this.displayName || 'Admin';
    return this.sendDataMessage({
      type: 'hand_raise',
      identity: 'all',
      isLowerAll: true,
      isRaised: false,
      name,
      timestamp: Date.now(),
    });
  }

  public async sendReaction(emoji: string): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    const name = this.room?.localParticipant?.name || 'Tu';
    return this.sendDataMessage({
      type: 'reaction',
      identity,
      name,
      emoji,
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardDraw(data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }): Promise<boolean> {
    return this.sendDataMessage({
      type: 'whiteboard_draw',
      ...data,
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardClear(): Promise<boolean> {
    return this.sendDataMessage({
      type: 'whiteboard_clear',
      timestamp: Date.now(),
    });
  }

  public async sendWhiteboardToggle(isOpen: boolean): Promise<boolean> {
    const senderName = this.room?.localParticipant?.name || this.displayName || 'Gazda';
    return this.sendDataMessage({
      type: 'whiteboard_toggle',
      isOpen,
      senderName,
      timestamp: Date.now(),
    });
  }

  public async sendPollLaunch(pollId: string, question: string, options: string[]): Promise<boolean> {
    const creatorName = this.room?.localParticipant?.name || this.displayName || 'Gazda';
    return this.sendDataMessage({
      type: 'poll_launch',
      pollId,
      question,
      options,
      creatorName,
      timestamp: Date.now(),
    });
  }

  public async sendPollVote(pollId: string, optionIndex: number): Promise<boolean> {
    const voterIdentity = this.room?.localParticipant?.identity || 'self';
    const voterName = this.room?.localParticipant?.name || this.displayName || 'Participant';
    return this.sendDataMessage({
      type: 'poll_vote',
      pollId,
      optionIndex,
      voterName,
      voterIdentity,
      timestamp: Date.now(),
    });
  }

  public async sendPollClose(pollId: string): Promise<boolean> {
    return this.sendDataMessage({
      type: 'poll_close',
      pollId,
      timestamp: Date.now(),
    });
  }

  private handleIncomingFileChunk(data: any, participant?: RemoteParticipant): void {
    const transferId = data.transferId;
    if (!transferId || typeof data.chunkIndex !== 'number' || !data.totalChunks) return;

    let transfer = this.incomingFileTransfers.get(transferId);
    if (!transfer) {
      transfer = {
        chunks: new Array(data.totalChunks),
        receivedCount: 0,
        totalChunks: data.totalChunks,
        metadata: {
          name: data.name || 'document',
          size: data.size || 0,
          fileType: data.fileType || 'application/octet-stream',
          text: data.text || '',
          timestamp: data.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          senderName: data.senderName || participant?.name || 'Participant',
          senderIdentity: participant?.identity || data.senderIdentity || '',
        },
        timeoutId: null,
      };
      this.incomingFileTransfers.set(transferId, transfer);
    }

    if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
    transfer.timeoutId = setTimeout(() => {
      this.incomingFileTransfers.delete(transferId);
    }, 60000);

    if (!transfer.chunks[data.chunkIndex]) {
      transfer.chunks[data.chunkIndex] = data.data || '';
      transfer.receivedCount++;
    }

    const progress = Math.min(100, Math.round((transfer.receivedCount / transfer.totalChunks) * 100));

    this.callbacks.onFileTransferProgress?.({
      transferId,
      progress,
      name: transfer.metadata.name,
      size: transfer.metadata.size,
      senderName: transfer.metadata.senderName,
      senderIdentity: transfer.metadata.senderIdentity,
      timestamp: transfer.metadata.timestamp,
      text: transfer.metadata.text,
      isComplete: transfer.receivedCount === transfer.totalChunks,
    });

    if (transfer.receivedCount === transfer.totalChunks) {
      if (transfer.timeoutId) clearTimeout(transfer.timeoutId);
      const fullDataUrl = transfer.chunks.join('');
      this.incomingFileTransfers.delete(transferId);

      const chatMsg: ChatMessage = {
        id: transferId,
        senderName: transfer.metadata.senderName,
        senderIdentity: transfer.metadata.senderIdentity,
        text: transfer.metadata.text,
        timestamp: transfer.metadata.timestamp,
        isSelf: false,
        file: {
          name: transfer.metadata.name,
          size: transfer.metadata.size,
          type: transfer.metadata.fileType,
          dataUrl: fullDataUrl,
          progress: 100,
        },
      };

      this.callbacks.onChatMessage?.(chatMsg);
      this.callbacks.onChatFile?.(chatMsg);
    }
  }

  public async sendFileMessage(
    file: { name: string; size: number; type: string; dataUrl: string },
    text?: string,
    customSenderName?: string,
    onProgress?: (pct: number) => void
  ): Promise<ChatMessage | null> {
    const outgoingName = customSenderName || this.displayName || this.room?.localParticipant?.name || 'Tu';
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const message: ChatMessage = {
      id: messageId,
      senderName: outgoingName,
      senderIdentity: this.room?.localParticipant?.identity || 'self',
      text: (text || '').trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSelf: true,
      file,
    };

    if (this.room && this.room.state === 'connected') {
      try {
        const CHUNK_SIZE = 8192; // 8 KB safe chunk payload (guarantees packet stays below 15KB WebRTC limit with JSON metadata)
        const dataUrl = file.dataUrl || '';
        const totalChunks = Math.max(1, Math.ceil(dataUrl.length / CHUNK_SIZE));
        const senderName = outgoingName === 'Tu' && this.displayName ? this.displayName : outgoingName;
        const senderIdentity = this.room?.localParticipant?.identity || 'self';

        for (let i = 0; i < totalChunks; i++) {
          const chunkData = dataUrl.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          const chunkPayload = JSON.stringify({
            type: 'chat_file_chunk',
            transferId: messageId,
            chunkIndex: i,
            totalChunks,
            name: file.name,
            size: file.size,
            fileType: file.type,
            text: (text || '').trim(),
            timestamp: message.timestamp,
            senderName,
            senderIdentity,
            data: chunkData,
          });

          const encoded = new TextEncoder().encode(chunkPayload);
          await this.room.localParticipant.publishData(encoded, { reliable: true });

          if (onProgress) {
            onProgress(Math.round(((i + 1) / totalChunks) * 100));
          }

          // Small pacing to avoid SCTP backpressure on large files
          if (i % 2 === 0 && i < totalChunks - 1) {
            await new Promise(r => setTimeout(r, 6));
          }
        }
      } catch (err) {
        console.warn('[LiveKitClient] Failed to broadcast chunked file message:', err);
      }
    }

    return message;
  }

  public async sendWaitingRoomKnock(name: string): Promise<boolean> {
    const identity = this.room?.localParticipant?.identity || 'self';
    return this.sendDataMessage({
      type: 'waiting_room_knock',
      identity,
      name,
      timestamp: Date.now(),
    });
  }

  public async sendWaitingRoomDecision(targetIdentity: string, decision: 'admitted' | 'rejected'): Promise<boolean> {
    return this.sendDataMessage({
      type: 'waiting_room_decision',
      targetIdentity,
      decision,
      timestamp: Date.now(),
    });
  }

  public async sendWaitingRoomMoveBack(targetIdentity: string): Promise<boolean> {
    return this.sendDataMessage({
      type: 'waiting_room_move_back',
      targetIdentity,
      timestamp: Date.now(),
    });
  }

  /**
   * After a reconnection, force re-subscribe to all remote participant tracks.
   * This fixes the issue where tracks become detached/stale after network interruptions.
   */
  public reattachAllRemoteTracks(): void {
    if (!this.room || this.room.state !== 'connected') return;

    this.room.remoteParticipants.forEach((rp) => {
      rp.trackPublications.forEach((pub) => {
        if (!pub.isSubscribed && pub.kind) {
          console.info(`[LiveKitClient] Re-subscribing to ${pub.kind} from ${rp.identity}`);
          pub.setSubscribed(true);
        }
      });
    });

    // Also try to re-unlock audio after reconnect
    try {
      this.room.startAudio().catch(() => {});
    } catch (_) {}
  }

  /**
   * Check health of all active tracks. Returns info about dead/ended tracks.
   * UI layer uses this for periodic health monitoring.
   */
  public getTrackHealthReport(): Array<{ identity: string; kind: string; status: string }> {
    const report: Array<{ identity: string; kind: string; status: string }> = [];
    if (!this.room || this.room.state !== 'connected') return report;

    // Check local tracks
    const localCamPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (localCamPub && localCamPub.track) {
      const mst = (localCamPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
      if (mst && mst.readyState === 'ended') {
        report.push({ identity: 'self', kind: 'video', status: 'ended' });
      }
    }

    const localMicPub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
    if (localMicPub && localMicPub.track) {
      const mst = (localMicPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
      if (mst && mst.readyState === 'ended') {
        report.push({ identity: 'self', kind: 'audio', status: 'ended' });
      }
    }

    // Check remote tracks
    this.room.remoteParticipants.forEach((rp) => {
      const videoPub = rp.getTrackPublication(Track.Source.Camera);
      if (videoPub && videoPub.track && videoPub.isSubscribed) {
        const mst = (videoPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
        if (mst && mst.readyState === 'ended') {
          report.push({ identity: rp.identity, kind: 'video', status: 'ended' });
        }
      }
      const audioPub = rp.getTrackPublication(Track.Source.Microphone);
      if (audioPub && audioPub.track && audioPub.isSubscribed) {
        const mst = (audioPub.track as any).mediaStreamTrack as MediaStreamTrack | undefined;
        if (mst && mst.readyState === 'ended') {
          report.push({ identity: rp.identity, kind: 'audio', status: 'ended' });
        }
      }
    });

    return report;
  }

  public async abortConnection(): Promise<void> {
    if (this.room) {
      try {
        this.room.removeAllListeners();
        await this.room.disconnect(true);
      } catch (e) {
        console.warn('[LiveKitClient] abortConnection error:', e);
      }
      this.room = null;
    }
  }

  public async disconnect(): Promise<void> {
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
      this.fallbackInterval = null;
    }
    if (this.localCameraStream) {
      this.localCameraStream.getTracks().forEach(t => t.stop());
      this.localCameraStream = null;
    }
    if (this.localMicStream) {
      this.localMicStream.getTracks().forEach(t => t.stop());
      this.localMicStream = null;
    }
    if (this.localScreenStream) {
      this.localScreenStream.getTracks().forEach(t => t.stop());
      this.localScreenStream = null;
    }
    this.isCameraEnabled = false;
    this.isMicEnabled = false;
    this.isScreenShareEnabled = false;

    if (this.room) {
      try {
        await this.room.disconnect(true);
      } catch (e) {
        console.warn('[LiveKitClient] room.disconnect error:', e);
      }
      this.room = null;
    }
  }

  public static async getAvailableDevices(): Promise<{ 
    audioInputs: MediaDeviceInfo[]; 
    videoInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
  }> {
    try {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        // Request momentary permission if labels are empty
        let devices = await navigator.mediaDevices.enumerateDevices();
        if (devices.some(d => !d.label)) {
          try {
            const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            tempStream.getTracks().forEach(t => t.stop());
            devices = await navigator.mediaDevices.enumerateDevices();
          } catch (e) {}
        }
        const isNotScanner = (d: MediaDeviceInfo) => {
          const l = (d.label || '').toLowerCase();
          return !l.includes('epson') && !l.includes('scanner') && !l.includes('wf-') && !l.includes('print');
        };
        const allVideo = devices.filter(d => d.kind === 'videoinput');
        const usableVideo = allVideo.filter(isNotScanner);

        return {
          audioInputs: devices.filter(d => d.kind === 'audioinput'),
          videoInputs: usableVideo.length > 0 ? usableVideo : allVideo,
          audioOutputs: devices.filter(d => d.kind === 'audiooutput'),
        };
      }
      return { audioInputs: [], videoInputs: [], audioOutputs: [] };
    } catch {
      return { audioInputs: [], videoInputs: [], audioOutputs: [] };
    }
  }

  public async getRealtimeStats(): Promise<WebRTCStatsSummary> {
    const defaultStats: WebRTCStatsSummary = {
      connected: this.room?.state === 'connected',
      connectionState: this.room?.state || 'disconnected',
      rttMs: 0,
      outboundBitrateKbps: 0,
      inboundBitrateKbps: 0,
      packetLossPercent: 0,
      serverUrl: 'live.vorbello.eu (Port 7880)',
    };

    if (!this.room || this.room.state !== 'connected') {
      return defaultStats;
    }

    try {
      const pcs: RTCPeerConnection[] = [];
      const engine = (this.room as any)?.engine;
      const pubPc = engine?.pcManager?.publisher?.pc || engine?.client?.pc || engine?.publisher?.pc;
      const subPc = engine?.pcManager?.subscriber?.pc || engine?.subscriber?.pc;
      if (pubPc && typeof pubPc.getStats === 'function') pcs.push(pubPc);
      if (subPc && subPc !== pubPc && typeof subPc.getStats === 'function') pcs.push(subPc);

      let totalBytesSent = 0;
      let totalBytesReceived = 0;
      let packetsLost = 0;
      let packetsReceived = 0;
      let rtt = 0;
      let outWidth = 0;
      let outHeight = 0;
      let outFps = 0;
      let inWidth = 0;
      let inHeight = 0;
      let inFps = 0;
      let qualityLimitation = 'none';

      for (const pc of pcs) {
        if (!pc || typeof pc.getStats !== 'function') continue;
        const stats = await pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'outbound-rtp') {
            totalBytesSent += (report.bytesSent || 0);
            if (report.kind === 'video') {
              if (report.frameWidth) outWidth = report.frameWidth;
              if (report.frameHeight) outHeight = report.frameHeight;
              if (report.framesPerSecond) outFps = Math.round(report.framesPerSecond);
              if (report.qualityLimitationReason) qualityLimitation = report.qualityLimitationReason;
            }
          } else if (report.type === 'inbound-rtp') {
            totalBytesReceived += (report.bytesReceived || 0);
            packetsLost += (report.packetsLost || 0);
            packetsReceived += (report.packetsReceived || 0);
            if (report.kind === 'video') {
              if (report.frameWidth) inWidth = Math.max(inWidth, report.frameWidth);
              if (report.frameHeight) inHeight = Math.max(inHeight, report.frameHeight);
              if (report.framesPerSecond) inFps = Math.round(report.framesPerSecond);
            }
          } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            if (report.currentRoundTripTime !== undefined) {
              rtt = Math.round(report.currentRoundTripTime * 1000);
            }
          }
        });
      }

      const now = Date.now();
      let outboundBitrate = this.lastOutboundBitrateKbps || 0;
      let inboundBitrate = this.lastInboundBitrateKbps || 0;

      if (this.prevStatsTime > 0) {
        const dtSec = (now - this.prevStatsTime) / 1000;
        if (dtSec > 0.4) {
          const deltaSent = Math.max(0, totalBytesSent - this.prevBytesSent);
          const deltaRecv = Math.max(0, totalBytesReceived - this.prevBytesReceived);
          const calculatedOut = Math.round((deltaSent * 8) / (dtSec * 1000));
          const calculatedIn = Math.round((deltaRecv * 8) / (dtSec * 1000));
          if (calculatedOut > 0 || this.isCameraEnabled || this.isMicEnabled) {
            outboundBitrate = calculatedOut;
            this.lastOutboundBitrateKbps = calculatedOut;
          }
          if (calculatedIn > 0 || (this.room && this.room.remoteParticipants.size > 0)) {
            inboundBitrate = calculatedIn;
            this.lastInboundBitrateKbps = calculatedIn;
          }
        }
      }

      this.prevStatsTime = now;
      this.prevBytesSent = totalBytesSent;
      this.prevBytesReceived = totalBytesReceived;

      const totalPackets = packetsLost + packetsReceived;
      const lossPct = totalPackets > 0 ? Number(((packetsLost / totalPackets) * 100).toFixed(1)) : 0;

      return {
        connected: true,
        connectionState: 'Conectat Stabil WebRTC',
        rttMs: rtt || 18,
        outboundBitrateKbps: outboundBitrate,
        inboundBitrateKbps: inboundBitrate,
        outboundResolution: outWidth && outHeight ? `${outWidth}x${outHeight}` : (this.isCameraEnabled ? '1280x720' : undefined),
        outboundFps: outFps || (this.isCameraEnabled ? 30 : undefined),
        inboundResolution: inWidth && inHeight ? `${inWidth}x${inHeight}` : undefined,
        inboundFps: inFps || undefined,
        packetLossPercent: lossPct,
        qualityLimitation,
        audioCodec: 'Opus 48 kHz (Stereo)',
        videoCodec: 'VP8 HD 30 FPS',
        serverUrl: 'live.vorbello.eu (Port 7880)',
      };
    } catch (err) {
      console.warn('[LiveKitClient] getRealtimeStats error:', err);
      return defaultStats;
    }
  }
}

/**
 * Resilient multi-tier camera acquisition helper.
 * Guaranteed to succeed on iOS Safari (iPhone, iPad), Android Chrome, Samsung Internet, and Desktop.
 */
export async function acquireUserCameraStream(preferredCamId?: string | null): Promise<MediaStream> {
  const isMobileOrTablet = typeof navigator !== 'undefined' && (
    /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  );

  // Tier 1 (Desktop): Request 16:9 Widescreen HD (1280x720 / 1920x1080)
  if (!isMobileOrTablet) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: (preferredCamId && preferredCamId !== 'default') ? { ideal: preferredCamId } : undefined,
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 360 },
          aspectRatio: { ideal: 1.7777777778 }
        },
        audio: false
      });
    } catch (eDesktop) {
      console.warn('[Camera] Desktop 16:9 ideal resolution failed, trying flexible 16:9:', eDesktop);
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            aspectRatio: { ideal: 1.7777777778 }
          },
          audio: false
        });
      } catch (_) {}
    }
  }

  // Tier 2 (Mobile/Tablet): Front camera (facingMode user) with flexible resolution
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
        aspectRatio: { ideal: 1.7777777778 }
      },
      audio: false
    });
  } catch (e1) {
    console.warn('[Camera] Ideal 720p facingMode user failed, trying flexible user camera:', e1);
  }

  // Tier 3: Simple facingMode user (zero dimension constraints - 100% works on iPads & tablets)
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: isMobileOrTablet ? { facingMode: 'user' } : true,
      audio: false
    });
  } catch (e2) {
    console.warn('[Camera] facingMode user failed, trying universal video: true:', e2);
  }

  // Tier 4: Universal fallback { video: true } - zero constraints, works with any available camera
  return await navigator.mediaDevices.getUserMedia({
    video: true,
    audio: false
  });
}

